import Box from '@material-ui/core/Box';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import { useContextCheckboxWithOptionalContent } from 'components/CheckboxWithOptionalContent';
import Popper, { MessageType } from 'components/Popper';
import { useContextRadioButtonWithOptionalContent } from 'components/RadioButtonWithOptionalContent';
import { FieldInputProps, getIn, useFormikContext } from 'formik';
import { isNil } from 'lodash';
import React, { ReactElement, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

export const TextFieldStyle = styled(TextField)`
  margin: 0 !important;
  .MuiInputLabel-outlined {
    z-index: 1;
    color: ${(props) => props.theme.colors.comet} !important;
    pointer-events: none;
    top: -3px;
    &::placeholder {
      color: ${(props) => props.theme.colors.dolphin};
    }
    &.MuiInputLabel-shrink {
      top: 2px;
      &.Mui-focused {
        color: ${(props) => props.theme.colors.comet} !important;
      }
    }
    &.Mui-disabled {
      color: ${(props) => props.theme.colors.nero};
      -webkit-text-fill-color: ${(props) => props.theme.colors.nero};
      background: transparent;
    }
  }

  .MuiInputLabel-root {
    font-weight: normal;
    font-size: 14px;
    line-height: 19px;
  }

  .MuiInputBase-root {
    font-family: Poppins !important;
    border-radius: 8px;
    padding: 12px;
    background: ${(props) => props.theme.colors.white};
    input {
      color: ${(props) => props.theme.colors.nero};
    }
    input[type='number']::-webkit-inner-spin-button,
    input[type='number']::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    &.MuiOutlinedInput-root {
      min-height: 43px;
      span {
        color: ${(props) => props.theme.colors.dolphin};
      }
    }
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${(props) => props.theme.colors.malibu};
      border-width: 1px;
    }
    &.MuiOutlinedInput-root:hover,
    &.MuiOutlinedInput-root:hover:focus {
      .MuiOutlinedInput-notchedOutline {
        border-color: ${(props) => props.theme.colors.malibu};
      }
    }
    &.MuiOutlinedInput-root .MuiInputBase-input.MuiOutlinedInput-input {
      padding: 0;
    }
    &.Mui-disabled {
      background: ${(props) => props.theme.colors.solitude};
      border-color: ${(props) => props.theme.colors.solitude};
      color: ${(props) => props.theme.colors.nero};
      &.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
        border-color: ${(props) => props.theme.colors.solitude};
      }
      &.MuiOutlinedInput-root:hover {
        .MuiOutlinedInput-notchedOutline {
          border-color: ${(props) => props.theme.colors.solitude};
        }
      }
      .MuiOutlinedInput-inputMultiline {
        font-size: 14px;
      }
    }
    .MuiOutlinedInput-notchedOutline {
      border-color: ${(props) => props.theme.colors.ghost};
    }
    .MuiInputAdornment-positionStart {
      font-family: Poppins !important;
    }
  }
  .MuiOutlinedInput-input.Mui-disabled {
    -webkit-text-fill-color: ${(props) => props.theme.colors.nero};
  }
  label.MuiFormLabel-filled + .MuiInputBase-root {
    legend {
      padding: 0 5px;
    }
  }
  .MuiFormHelperText-root {
    &.Mui-error {
      color: ${(props) => props.theme.colors.radicalRed};
      position: absolute;
      bottom: -15px;
    }
  }
  ${({ label }) =>
    !label
      ? `
        legend {
          width: 0;
        }
      `
      : ''}
`;

const TextFieldWrapperStyle = styled(Box)``;
export const Label = styled(Box)`
  margin-bottom: 4px;
  font-size: 14px;
  color: ${(props) => props.theme.colors.dolphin};
  line-height: 1.3em;
  font-weight: 500;
`;

export type Props = {
  form?: any;
  editable?: boolean;
  helperText?: string;
  focusField?: boolean;
  // "string" type use with RatingScaleWithCustomText
  focusChanged?: boolean | string;
  alertVariant?: 'info' | 'warning' | 'success' | 'error';
  field: FieldInputProps<any>;
  suffix?: ReactElement;
  prefix?: string | ReactElement;
  rows?: number | undefined;
  label?: string;
  labelPosition?: 'top' | 'left';
  widthAuto?: boolean;
  type?: string;
  value?: string;
  onClick?: (event: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
} & TextFieldProps;

const TextInput: React.VFC<Props> = ({
  form,
  editable = false,
  focusField,
  focusChanged,
  alertVariant,
  field,
  suffix,
  prefix,
  multiline,
  rows,
  disabled,
  label,
  labelPosition,
  widthAuto = false,
  onChange,
  required,
  placeholder,
  type,
  ...props
}) => {
  const { checked, hasChecked } = useContextCheckboxWithOptionalContent();
  const { valueChecked, hasSelected } = useContextRadioButtonWithOptionalContent();

  const inputRef = useRef<HTMLInputElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const isUserChecked = checked && hasChecked;
    const isUserSelected = valueChecked && hasSelected;

    if ((isUserChecked || isUserSelected || focusChanged) && focusField) {
      if (inputRef.current) {
        inputRef.current.focus();
      } else if (textAreaRef.current) {
        textAreaRef.current.focus();
      }
    }
  }, [
    checked,
    focusField,
    valueChecked,
    focusChanged,
    field.value,
    hasChecked,
    hasSelected,
    field.name,
  ]);

  useEffect(() => {
    if (checked !== undefined && !checked && form && field.value) {
      form.setFieldValue(field.name, undefined);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }

    if (valueChecked !== undefined && !valueChecked && form && field.value) {
      form.setFieldValue(field.name, undefined);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  }, [checked, valueChecked, field, form]);

  const displayInput = useCallback(() => {
    const handleCheckShrinkShow = () => {
      if (!editable) {
        return true;
      }

      if (type === 'number') {
        return !!field.value || field.value === 0;
      }

      return !!field.value;
    };

    return (
      <TextFieldStyle
        inputRef={inputRef}
        variant="outlined"
        disabled={!editable || disabled}
        fullWidth
        type={type}
        InputLabelProps={{
          shrink: handleCheckShrinkShow(),
        }}
        {...field}
        value={!isNil(field.value) ? field.value : ''}
        {...props}
        onChange={onChange || field.onChange}
        onKeyDown={(e) => {
          const invalidChars = ['-', '+', 'e'];
          if (invalidChars.includes(e.key) && type === 'number') {
            e.preventDefault();
          }
        }}
        InputProps={{
          endAdornment: suffix,
          startAdornment: prefix ? (
            <InputAdornment position="start">{prefix}</InputAdornment>
          ) : null,
        }}
        minRows={multiline ? rows : 1}
        label={labelPosition ? undefined : label}
        placeholder={editable ? placeholder : ''}
        multiline={multiline === undefined ? type !== 'number' : multiline}
        name={field.name}
      />
    );
  }, [
    disabled,
    editable,
    field,
    label,
    labelPosition,
    multiline,
    onChange,
    placeholder,
    prefix,
    props,
    rows,
    suffix,
    type,
  ]);

  return (
    <TextFieldWrapperStyle
      display={labelPosition === 'left' ? 'flex' : 'block'}
      alignItems="center"
      width={widthAuto ? 'auto' : '100%'}
    >
      {labelPosition ? <Label mr={labelPosition === 'left' ? 1 : 0}>{label}</Label> : null}
      {displayInput()}
    </TextFieldWrapperStyle>
  );
};

const AdaptMgTextInput: React.VFC<Props & TextFieldProps> = (props) => {
  return (
    <Box
      sx={{
        py: 1.25,
      }}
    >
      <TextInput {...props} />
    </Box>
  );
};

const AdaptErrorTextInput: React.VFC<{ errorMsgType?: MessageType } & Props & TextFieldProps> = ({
  errorMsgType,
  ...props
}) => {
  const { errors } = useFormikContext<any>();
  const errorMsg = getIn(errors, props.field.name);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 1.25,
      }}
    >
      <Box
        sx={{
          flex: 1,
        }}
      >
        <TextInput {...props} />
      </Box>
      {errorMsg ? (
        <Popper
          id={`${props.field.name}-popper-svg`}
          type={errorMsgType ?? MessageType.ERROR}
          msg={errorMsg}
          style={{
            paddingLeft: 6,
          }}
        />
      ) : null}
    </Box>
  );
};

export default TextInput;
export { AdaptMgTextInput, AdaptErrorTextInput };
